import React, { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CameraProvisionService, { GcpProvisionedStatus } from '../../services/CameraProvisionService'
import StreamingService from '../../services/StreamingService'

interface Props {
  cameraId: number
  cameraName: string
}

const STATUS_LABELS: Record<NonNullable<GcpProvisionedStatus>, { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
  STOPPED: { label: 'Parado', color: 'default' },
  STARTING: { label: 'Iniciando…', color: 'info' },
  RUNNING: { label: 'Em transmissão', color: 'success' },
  STOPPING: { label: 'Parando…', color: 'warning' },
  AWAITING_INPUT: { label: 'Aguardando entrada', color: 'warning' },
}

const TRANSITIONING: GcpProvisionedStatus[] = ['STARTING', 'STOPPING', 'AWAITING_INPUT']

export default function CameraProvisionStatus({ cameraId, cameraName }: Props) {
  const [status, setStatus] = useState<GcpProvisionedStatus>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [channel, setChannel] = useState<string | null>(null)

  const fetchStatus = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await CameraProvisionService.getProvisionedStatus(cameraId)
      setStatus(res?.data?.status ?? null)
      setChannel(res?.data?.channel ?? null)
    } catch {
      setError('Não foi possível obter o status de provisão.')
    } finally {
      setLoading(false)
    }
  }, [cameraId])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  const handleAction = async (action: () => Promise<any>, successMessage?: string) => {
    setActionLoading(true)
    setError(null)
    try {
      await action()
      await fetchStatus()
    } catch {
      setError(successMessage ?? 'Ocorreu um erro ao executar a ação.')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1} py={1}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Verificando status…
        </Typography>
      </Box>
    )
  }

  const isTransitioning = status !== null && TRANSITIONING.includes(status)
  const statusInfo = status ? STATUS_LABELS[status] : null

  return (
    <Box py={1}>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Status GCP — {status}
          </Typography>
          {statusInfo ? (
            <Chip
              size="small"
              label={statusInfo.label}
              color={statusInfo.color}
              sx={{ mt: 0.5 }}
            />
          ) : (
            <Chip size="small" label="Não provisionado" color="default" sx={{ mt: 0.5 }} />
          )}
        </Box>

        <Divider orientation="vertical" flexItem />

        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          {/* Não provisionado — exibe botão de provisionar */}
          {!status && (
            <Button
              variant="contained"
              size="small"
              color="primary"
              startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : <AddCircleOutlineIcon />}
              disabled={actionLoading}
              onClick={() =>
                handleAction(
                  () => StreamingService.provision(cameraId),
                  'Erro ao provisionar câmera.'
                )
              }
            >
              Provisionar
            </Button>
          )}

          {/* STOPPED — exibe botão de iniciar transmissão */}
          {status === 'STOPPED' && (
            <Button
              variant="contained"
              size="small"
              color="success"
              startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
              disabled={actionLoading}
              onClick={() =>
                handleAction(
                  () => StreamingService.startChannel(cameraId),
                  'Erro ao iniciar transmissão.'
                )
              }
            >
              Iniciar Canal
            </Button>
          )}

          {/* RUNNING ou AWAITING_INPUT — exibe botão de parar transmissão */}
          {(status === 'RUNNING' || status === 'AWAITING_INPUT') && (
            <Button
              variant="contained"
              size="small"
              color="error"
              startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : <StopIcon />}
              disabled={actionLoading}
              onClick={() =>
                handleAction(
                  () => StreamingService.stopChannel(cameraId),
                  'Erro ao parar transmissão.'
                )
              }
            >
              Parar Canal
            </Button>
          )}

          {/* Estados de transição — sem ação disponível */}
          {isTransitioning && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <HourglassEmptyIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Aguarde…
              </Typography>
            </Box>
          )}

          {/* Botão de atualizar status */}
          <Button
            variant="outlined"
            size="small"
            disabled={actionLoading || loading}
            onClick={fetchStatus}
          >
            Atualizar
          </Button>

          {/* Botão de Desprovisionar — sempre disponível, mas com confirmação */}
          {channel !== null && status !== 'AWAITING_INPUT' && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              disabled={actionLoading || loading}
              onClick={() => handleAction(() => StreamingService.deprovision(cameraId), 'Erro ao desprovisionar câmera.')}
            >
              Desprovisionar
            </Button>
          )}

          {/* Botão Stop Streaming — apenas para canais em RUNNING */}
          {status === 'RUNNING' && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              disabled={actionLoading || loading}
              onClick={() => handleAction(() => CameraProvisionService.stopChannel(cameraId), 'Erro ao parar transmissão.')}
            >
              Parar Streaming
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Box>
  )
}
